import {ALLOW_COUNTRIES} from "@hosttools/core/shared/validatePhone";
import type {FC} from "react";
import {memo, useCallback, useEffect, useMemo, useRef, useState} from "react";
import type {NativeSyntheticEvent, TextInputKeyPressEventData} from "react-native";

import PhoneInput from "../PhoneInput";
import {HStack} from "../ui/hstack";
import {Input, InputField, InputRightAddon} from "../ui/input";
import {VStack} from "../ui/vstack";

import TagItem from "./TagItem";
import type {InputType, TagModel} from "./type";

interface Props {
    initTags: string[];
    inputType: InputType;
    validityCheck?: (value: string) => boolean;
    onChange?: (values: string[]) => any;
    maxLength?: number;
    newTagLabel?: string;
}

const INIT_COUNTRY = "US";

function isValidNumberPhone(value: string): boolean {
    return value.length > 10;
}

export const TagInput: FC<Props> = ({
    initTags,
    inputType,
    newTagLabel = "New Tag",
    maxLength,
    validityCheck,
    onChange
}) => {
    const checkValidity = useCallback(
        (value: string): boolean => {
            if (inputType === "phone") {
                return validityCheck ? validityCheck(value) : isValidNumberPhone(value);
            }
            return validityCheck ? validityCheck(value) : true;
        },
        [inputType, validityCheck]
    );

    const tagInputRef = useRef<React.ElementRef<typeof Input>>(null);
    const phoneInputRef = useRef<React.ElementRef<typeof PhoneInput>>(null);
    const [tags, setTags] = useState<TagModel[]>(
        initTags.map(value => ({
            value,
            label: value,
            valid: checkValidity(value)
        }))
    );
    const [editInputIndex, setEditInputIndex] = useState<number>();
    const [inputValue, setInputValue] = useState<TagModel>();

    const isReachLimit = useMemo(() => {
        if (typeof maxLength === "number" && tags.length >= maxLength) {
            return true;
        }
        return false;
    }, [maxLength, tags.length]);

    const invokeChangeCallback = useCallback(
        (newTags: TagModel[]) => {
            onChange?.(newTags.map(elem => elem.value));
        },
        [onChange]
    );

    const handleRemove = useCallback(
        (removedTag: TagModel) => {
            const newTags = tags.filter(tag => tag.value !== removedTag.value);

            setTags(newTags);
            invokeChangeCallback(newTags);
        },
        [invokeChangeCallback, tags]
    );

    const handleInputChange = (value: string) => {
        setInputValue({
            value,
            label: value
        });
    };

    const handlePhoneInputChange = useCallback((value: string, inputValue: string) => {
        setInputValue({
            value,
            label: inputValue
        });
    }, []);

    const doEditTag = useCallback(() => {
        if (
            typeof editInputIndex !== "undefined" &&
            inputValue &&
            !tags.some(elem => elem.value === inputValue.value)
        ) {
            const newTags = [...tags];

            newTags[editInputIndex] = {
                ...inputValue,
                valid: checkValidity(inputValue.value)
            };

            setTags(newTags);
            invokeChangeCallback(newTags);
        }

        setInputValue(undefined);
        setEditInputIndex(undefined);
        phoneInputRef.current?.setInputValue("");
    }, [checkValidity, editInputIndex, inputValue, invokeChangeCallback, tags]);

    const doAddTag = useCallback(() => {
        if (inputValue && !isReachLimit) {
            if (!tags.some(elem => elem.value === inputValue.value)) {
                const newTags: TagModel[] = [
                    ...tags,
                    {
                        ...inputValue,
                        valid: checkValidity(inputValue.value)
                    }
                ];

                setTags(newTags);
                invokeChangeCallback(newTags);
            }
        }
        setInputValue(undefined);
        phoneInputRef.current?.setInputValue("");
    }, [checkValidity, inputValue, invokeChangeCallback, isReachLimit, tags]);

    const handleInputKeydown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            const {key} = e;
            if (key === "Enter") {
                if (typeof editInputIndex !== "undefined") {
                    doEditTag();
                } else {
                    doAddTag();
                }
            }
        },
        [doAddTag, doEditTag, editInputIndex]
    );

    const handlePhoneInputKeyPress = useCallback(
        (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
            const {key} = e.nativeEvent;
            if (key === "Enter") {
                if (typeof editInputIndex !== "undefined") {
                    doEditTag();
                } else {
                    doAddTag();
                }
            }
        },
        [doAddTag, doEditTag, editInputIndex]
    );

    const handleBlurInput = useCallback(() => {
        if (typeof editInputIndex !== "undefined") {
            doEditTag();
            return;
        }
        doAddTag();
    }, [doAddTag, doEditTag, editInputIndex]);

    const handleClickTag = useCallback(
        (index: number, tag: TagModel) => {
            setEditInputIndex(index);
            setInputValue(tag);

            if (inputType === "phone") {
                phoneInputRef.current?.setInputValue(tag.value);
            }
        },
        [inputType]
    );

    useEffect(() => {
        if (typeof editInputIndex !== "undefined") {
            if (
                process.env.IS_WEB &&
                inputType === "text" &&
                tagInputRef.current instanceof HTMLInputElement
            ) {
                tagInputRef.current.focus();
            }
        }
    }, [editInputIndex, inputType]);

    return (
        <VStack space="sm">
            {inputType === "phone" ? (
                <PhoneInput
                    ref={phoneInputRef}
                    country={INIT_COUNTRY}
                    defaultValue={inputValue?.value}
                    isDisabled={isReachLimit && typeof editInputIndex === "undefined"}
                    isEditing={typeof editInputIndex !== "undefined"}
                    onlyCountries={ALLOW_COUNTRIES}
                    showAddNew={typeof editInputIndex === "undefined" && !isReachLimit}
                    onChange={handlePhoneInputChange}
                    onClickAddNew={doAddTag}
                    onDone={doEditTag}
                    onKeyPress={handlePhoneInputKeyPress}
                />
            ) : (
                <Input
                    ref={tagInputRef}
                    isDisabled={isReachLimit && typeof editInputIndex === "undefined"}
                >
                    <InputField<"web">
                        placeholder={newTagLabel}
                        value={inputValue?.label || ""}
                        onBlur={handleBlurInput}
                        onChangeText={handleInputChange}
                        onKeyPress={handleInputKeydown}
                    />
                    {!editInputIndex && !isReachLimit && (
                        <InputRightAddon onPress={doAddTag}>+ Add</InputRightAddon>
                    )}
                </Input>
            )}
            <HStack className="flex-wrap" space="xs">
                {tags.map((tag, index) => {
                    return (
                        <TagItem
                            key={tag.value}
                            index={index}
                            inputType={inputType}
                            tag={tag}
                            onClickTag={handleClickTag}
                            onClose={handleRemove}
                        />
                    );
                })}
            </HStack>
        </VStack>
    );
};

export default memo(TagInput);
